import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { linkTierChangeMessage } from "utilities/tierInheritance";
import { Search, Building2, X, Check } from "lucide-react";

/**
 * Picker modal for linking a child company to a parent.
 * The `child` prop is the company that will become a child.
 */
export default function LinkParentCompanyModal({ child, onClose, onLinked }) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  // Debounce the search query
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCandidates(query);
    }, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const fetchCandidates = async (search) => {
    if (!effectiveCompanyId || !auth?.user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("companyType", "client");
      params.set("limit", "25");
      if (search) params.set("search", search);

      const res = await makeApiRequest(
        "get",
        `${apiRoutes.getCompanies}?${params.toString()}`,
        auth.user,
        effectiveCompanyId
      );
      const all = Array.isArray(res?.data?.data) ? res.data.data : [];
      // Client-side filters per SOP / plan:
      //   - not the child itself
      //   - not a company that already has a parent (no reverse nesting)
      //   - not "sound-admin" slug/id
      //   - companyType must be "client" (server also enforces)
      const filtered = all.filter((c) => {
        if (!c) return false;
        if (c.companyId === child.companyId) return false;
        if (c.parentCompanyId) return false;
        if (c.companyId === "sound-admin" || c.slug === "sound-admin") return false;
        if (c.companyType && c.companyType !== "client") return false;
        return true;
      });
      setResults(filtered);
    } catch (err) {
      console.error("[LinkParentCompanyModal] Search failed", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await makeApiRequest(
        "put",
        apiRoutes.updateCompany(child.companyId),
        auth.user,
        effectiveCompanyId,
        { parentCompanyId: selected.companyId }
      );
      // SOUND-795: linking under a Managed Services parent sets the child's
      // tier server-side. Report what the response ACTUALLY says (never a
      // prediction); the caller's onLinked → refetch() then repaints the page.
      const tierNote = linkTierChangeMessage({
        beforeTier: child.tier,
        after: res?.data,
      });
      toast({
        status: "success",
        title: "Parent linked",
        description: [
          `${child.name} is now a child of ${selected.name}.`,
          tierNote,
        ]
          .filter(Boolean)
          .join(" "),
      });
      onLinked();
    } catch (err) {
      console.error("[LinkParentCompanyModal] Link failed", err);
      toast({
        status: "destructive",
        title: "Failed to link parent",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-lg shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Link to Parent Company</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Pick the parent client for{" "}
              <span className="text-foreground font-medium">{child.name}</span>.
            </p>
          </div>
          <button
            className="btn btn-ghost h-8 w-8 p-0"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            className="input input-bordered w-full pl-9"
            placeholder="Search by name, slug, or company ID..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>

        <div className="max-h-80 overflow-y-auto -mx-2 px-2 mb-3">
          {loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Searching...
            </div>
          ) : results.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {query
                ? "No eligible companies found."
                : "Start typing to search, or pick from the list."}
            </div>
          ) : (
            <ul className="space-y-1">
              {results.map((c) => {
                const isSelected = selected?.companyId === c.companyId;
                return (
                  <li key={c.companyId}>
                    <button
                      type="button"
                      onClick={() => setSelected(c)}
                      className={`w-full flex items-center gap-3 text-left px-3 py-2 rounded-lg border transition-colors ${
                        isSelected
                          ? "border-brand bg-brand/10"
                          : "border-transparent hover:border-border hover:bg-muted/60"
                      }`}
                    >
                      <div
                        className={`p-1.5 rounded-md ${
                          isSelected ? "bg-brand/20" : "bg-muted"
                        }`}
                      >
                        <Building2
                          className={`h-4 w-4 ${
                            isSelected
                              ? "text-brand"
                              : "text-muted-foreground"
                          }`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {c.name}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {c.slug ? `${c.slug} · ` : ""}
                          {c.companyId}
                        </div>
                      </div>
                      {isSelected && (
                        <Check className="h-4 w-4 text-brand shrink-0" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex gap-2 pt-2 border-t border-border">
          <button
            className="btn btn-ghost flex-1"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary flex-1"
            onClick={handleConfirm}
            disabled={!selected || saving}
          >
            {saving
              ? "Linking..."
              : selected
              ? `Link to ${selected.name}`
              : "Select a parent"}
          </button>
        </div>
      </div>
    </div>
  );
}
